// EmailDeliveriesHistory — блок «История отправок» (V15 «Почта» W7, ТЗ §3.8).
// GET /api/email/deliveries (size=20, created_at DESC — RBAC: свои / admin — все).
// Таблица: дата / получатель / тема / статус-чип / ошибка; статусы — локализованные (U-16).
// body_preview ≤4000 — PII-минимизация; секретов нет по определению (V41).

import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { emailApi, type EmailDeliveryStatus } from '@/api/email';
import { formatDate } from '@/lib/format';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';

/** Страница «Истории отправок» (ТЗ §3.8: последние 20 доставок). */
const HISTORY_PAGE_SIZE = 20;

/** Цвет чипа статуса (SENT — success, FAILED — error, SKIPPED_* — warning). */
const STATUS_COLOR: Record<EmailDeliveryStatus, 'success' | 'error' | 'warning' | 'default'> = {
  SENT: 'success',
  FAILED: 'error',
  SKIPPED_NO_ACCOUNT: 'warning',
  SKIPPED_NO_RECIPIENT: 'warning',
  SKIPPED_RATE_LIMIT: 'warning',
};

/** Удаление записи из истории не предусмотрено (лог; TRUNCATE не выводим). */
function deliveryStatusLabel(status: EmailDeliveryStatus): string {
  return t(`email.deliveries.status.${status}`);
}

/** Блок «История отправок»: последние 20 доставок с чипами статусов. */
export function EmailDeliveriesHistory(): JSX.Element {
  const query = useQuery({
    queryKey: ['email', 'deliveries'],
    queryFn: () => emailApi.listDeliveries(0, HISTORY_PAGE_SIZE),
  });

  if (query.isLoading) {
    return <LoadingSpinner label={t('common.loading')} />;
  }
  if (query.isError) {
    return (
      <Alert severity="error">
        {query.error instanceof Error ? query.error.message : t('email.deliveries.loadError')}
      </Alert>
    );
  }
  const deliveries = query.data?.content ?? [];
  if (deliveries.length === 0) {
    return <EmptyState title={t('email.deliveries.empty')} description={t('email.deliveries.emptyDesc')} />;
  }

  return (
    <Box>
      <Table size="small" aria-label={t('email.tabs.deliveries')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('email.deliveries.date')}</TableCell>
            <TableCell>{t('email.deliveries.recipient')}</TableCell>
            <TableCell>{t('email.deliveries.subject')}</TableCell>
            <TableCell>{t('email.tabs.deliveries')}</TableCell>
            <TableCell>{t('email.deliveries.error')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deliveries.map((d) => (
            <TableRow key={d.id} hover>
              <TableCell>{formatDate(d.createdAt)}</TableCell>
              <TableCell>{d.recipientEmail}</TableCell>
              <TableCell>{d.subject}</TableCell>
              <TableCell>
                <Chip size="small" color={STATUS_COLOR[d.status] ?? 'default'} label={deliveryStatusLabel(d.status)} />
              </TableCell>
              <TableCell>{d.errorMessage ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
